import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import ProductTour from "@/components/ProductTour";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import {
  LayoutDashboard, Bot, GitFork, Wallet, MessagesSquare, ShoppingCart,
  ScrollText, ShieldAlert, LogOut, ShieldCheck, Snowflake, Users, Radio, Rocket,
  KeyRound, X, Loader2, CreditCard, TrendingUp, Megaphone, Radar, Activity, Target, Gauge, BookOpen,
} from "lucide-react";

const NAV = [
  { to: "/console", label: "Console & Telemetry", icon: LayoutDashboard, testid: "nav-console" },
  { to: "/agents", label: "Agents Fleet", icon: Bot, testid: "nav-agents" },
  { to: "/roi", label: "ROI & Economics", icon: TrendingUp, testid: "nav-roi" },
  { to: "/approvals", label: "Approval Queue", icon: ShieldCheck, testid: "nav-approvals" },
  { to: "/spawn-tree", label: "Spawn Tree", icon: GitFork, testid: "nav-spawn-tree" },
  { to: "/treasury", label: "Treasury & Approvals", icon: Wallet, testid: "nav-treasury" },
  { to: "/inbox", label: "Customer Inbox", icon: MessagesSquare, testid: "nav-inbox" },
  { to: "/orders", label: "Orders & Revenue", icon: ShoppingCart, testid: "nav-orders" },
  { to: "/deployments", label: "Deployment Pipeline", icon: Rocket, testid: "nav-deployments" },
  { to: "/ad-studio", label: "Ad Studio", icon: Megaphone, testid: "nav-ad-studio" },
  { to: "/lead-sources", label: "Lead Sources", icon: Radar, testid: "nav-lead-sources" },
  { to: "/outreach", label: "Local Outreach", icon: Target, testid: "nav-outreach" },
  { to: "/autonomy", label: "Autonomy", icon: Activity, testid: "nav-autonomy" },
  { to: "/launch", label: "Launch Readiness", icon: Gauge, testid: "nav-launch" },
  { to: "/guide", label: "Guide & Tutorial", icon: BookOpen, testid: "nav-guide" },
  { to: "/team", label: "Team Seats", icon: Users, testid: "nav-team" },
  { to: "/audit", label: "Audit & Forensics", icon: ScrollText, testid: "nav-audit" },
  { to: "/billing", label: "Billing & Plan", icon: CreditCard, testid: "nav-billing" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [billing, setBilling] = useState(null);
  useEffect(() => { api.get("/billing").then((r) => setBilling(r.data)).catch(() => {}); }, []);
  const isAdmin = user?.role === "admin";

  const loadWs = () => api.get("/workspace").then((r) => setWs(r.data)).catch(() => {});
  useEffect(() => {
    loadWs();
    const i = setInterval(loadWs, 15000);
    return () => clearInterval(i);
  }, []);
  useRealtime((msg) => {
    loadWs();
    if (msg?.type === "first_revenue") {
      fireConfetti();
      toast.success(`🎉 First revenue — ${money(msg.amount)}!`, {
        description: `${msg.agent || "An agent"} just booked your fleet's very first sale. It's in your treasury.`,
        duration: 9000,
      });
    }
    if (msg?.type === "milestone") {
      fireConfetti(true);
      const label = msg.milestone >= 1000 ? `$${(msg.milestone / 1000).toLocaleString()}k` : `$${msg.milestone}`;
      toast.success(`🏆 ${label} revenue milestone!`, {
        description: `Your fleet just crossed ${label} in cumulative earned revenue. Onward.`,
        duration: 9000,
      });
    }
  });

  const toggleFreeze = async () => {
    if (!ws) return;
    try {
      if (ws.frozen) {
        setWs({ ...ws, frozen: false });
        await api.post("/workspace/unfreeze");
        toast.success("Fleet freeze lifted — agents reactivated");
      } else {
        setWs({ ...ws, frozen: true });
        await api.post("/workspace/freeze");
        toast.error("EMERGENCY FLEET FREEZE engaged — all agents halted");
      }
      await loadWs();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#080B11] text-slate-100 flex">
      <ProductTour />
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-slate-800/80 bg-[#0A0E16] flex flex-col fixed h-screen" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-800/80">
          <div className="flex items-center gap-2.5">
            <img src="/kleeng-logo.png" alt="Kleen-G" className="w-9 h-9 rounded-md" />
            <div>
              <div className="font-display font-black text-sm tracking-tight leading-none">Kleen-G</div>
              <div className="font-mono text-[9px] text-slate-500 tracking-[0.15em] uppercase">Autonomous Agents</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-slate-800/80">
          <div className="px-2 mb-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="text-slate-300 font-medium truncate">{user?.name}</div>
              {billing && (
                <span data-testid="plan-badge" className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${billing.comped ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : billing.plan === "free" ? "bg-slate-700/50 text-slate-400 border border-slate-600" : "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"}`}>
                  {billing.comped ? "ORG ∞" : (billing.plan || "free").toUpperCase()}
                </span>
              )}
            </div>
            <div className="text-slate-500 font-mono text-[10px] truncate">{user?.email}</div>
          </div>
          {billing && !billing.comped && billing.agent_cap < 1e8 && billing.agents_used >= billing.agent_cap && (
            <button data-testid="upgrade-nudge" onClick={() => navigate("/billing")}
              className="w-full mb-2 flex items-center gap-2 px-3 py-2 rounded-md text-[11px] font-semibold bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 transition-colors">
              <CreditCard className="w-3.5 h-3.5" /> Agent limit reached — upgrade
            </button>
          )}
          <button
            data-testid="change-password-btn"
            onClick={() => setShowPwd(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
          >
            <KeyRound className="w-4 h-4" /> Change password
          </button>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col">
        <header className="h-16 shrink-0 border-b border-slate-800/80 backdrop-blur-xl bg-[#080B11]/85 sticky top-0 z-40 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">Workspace</span>
            <span className="font-display font-bold text-sm text-slate-100" data-testid="workspace-name">{ws?.name || "—"}</span>
            {ws?.legal_mode && (
              <span className="hidden md:inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono text-[9px] uppercase tracking-wider">
                <ShieldAlert className="w-3 h-3" /> Sandboxed · Admin Oversight
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/10" data-testid="live-indicator" title="Realtime connected">
              <Radio className="w-3 h-3 text-emerald-400" />
              <span className="font-mono text-[9px] uppercase tracking-wider text-emerald-400">Live</span>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-slate-700 text-slate-400" data-testid="user-role-badge">{user?.role}</span>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-700 bg-[#0E131F]" data-testid="header-treasury">
              <Wallet className="w-3.5 h-3.5 text-emerald-400" />
              <span className="font-mono font-bold tabular-nums text-sm text-emerald-400">{money(ws?.treasury_balance)}</span>
            </div>
            {isAdmin && (
              <button
                data-testid="fleet-freeze-btn"
                onClick={toggleFreeze}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md font-mono text-[10px] font-bold uppercase tracking-wider border transition-all duration-200 ${
                  ws?.frozen
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25"
                    : "bg-red-500/15 border-red-500/40 text-red-400 hover:bg-red-500/25"
                }`}
              >
                <Snowflake className="w-3.5 h-3.5" />
                {ws?.frozen ? "Lift Freeze" : "Fleet Freeze"}
              </button>
            )}
          </div>
        </header>

        <main className="flex-1 p-6 grid-bg min-h-[calc(100vh-4rem)]">
          <Outlet context={{ ws, reloadWs: loadWs, user }} />
        </main>
      </div>
    </div>
  );
}

const CONFETTI_COLORS = ["#06B6D4", "#10B981", "#F59E0B", "#F8FAFC"];

function fireConfetti(big = false) {
  try {
    const base = { spread: big ? 110 : 80, startVelocity: big ? 55 : 45, colors: CONFETTI_COLORS, zIndex: 9999, disableForReducedMotion: true };
    confetti({ ...base, particleCount: big ? 180 : 110, origin: { x: 0.2, y: 0.7 } });
    confetti({ ...base, particleCount: big ? 180 : 110, origin: { x: 0.8, y: 0.7 } });
    if (big) setTimeout(() => confetti({ ...base, particleCount: 120, origin: { x: 0.5, y: 0.5 } }), 220);
  } catch { /* confetti is non-critical */ }
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed successfully");
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm" data-testid="change-password-modal" onClick={onClose}>
      <div className="hud-card accent-top rounded-xl p-6 w-full max-w-sm relative" style={{ "--accent-line": "#06B6D4" }} onClick={(e) => e.stopPropagation()}>
        <button data-testid="change-password-close" onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-5 h-5 text-cyan-400" />
          <h2 className="font-display text-lg font-bold">Change password</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">Minimum 8 characters, with at least one letter and one number.</p>
        <form onSubmit={submit} className="space-y-4">
          <PwdField label="Current Password" testid="current-password-input" value={cur} onChange={(e) => setCur(e.target.value)} required />
          <PwdField label="New Password" testid="new-password-input" value={next} onChange={(e) => setNext(e.target.value)} required />
          <PwdField label="Confirm New Password" testid="confirm-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          {error && <div data-testid="change-password-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{error}</div>}
          <button type="submit" data-testid="change-password-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold hover:bg-cyan-400 transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PwdField({ label, testid, ...props }) {  return (
    <div>
      <label className="font-mono text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-400 mb-1.5 block">{label}</label>
      <input
        type="password"
        data-testid={testid}
        {...props}
        className="w-full px-3 py-2.5 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60 transition-colors"
      />
    </div>
  );
}
